import { api } from '../lib/api';
import type { User, AuthTokens } from '@gifting/shared';

interface AuthResponse {
  user: User;
  tokens: AuthTokens;
  isNewUser: boolean;
}

interface OTPRequestResponse {
  success: boolean;
  message: string;
}

/**
 * Request OTP for login
 */
export async function requestOTP(
  identifier: string,
  type: 'phone' | 'email'
): Promise<OTPRequestResponse> {
  const response = await api.post<OTPRequestResponse>('/auth/otp/request', {
    identifier,
    type,
  });
  return response.data!;
}

/**
 * Verify OTP and login
 */
export async function verifyOTP(
  identifier: string,
  otp: string,
  type: 'phone' | 'email'
): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/otp/verify', {
    identifier,
    otp,
    type,
  });
  return response.data!;
}

// OAuth methods commented out for auth overhaul — easy re-enable
// export async function loginWithGoogle(code: string, redirectUri?: string): Promise<AuthResponse> { ... }
// export async function loginWithLinkedIn(code: string, redirectUri?: string): Promise<AuthResponse> { ... }

/**
 * Refresh access token
 */
export async function refreshTokens(
  refreshToken: string
): Promise<AuthTokens> {
  const response = await api.post<AuthTokens>('/auth/refresh', {
    refreshToken,
  });
  return response.data!;
}

/**
 * Logout
 */
export async function logout(refreshToken?: string): Promise<void> {
  await api.post('/auth/logout', { refreshToken });
}

/**
 * Get current user profile
 */
export async function getMe(): Promise<{
  user: User;
  isProfileComplete: boolean;
  missingFields: string[];
}> {
  const response = await api.get<{
    user: User;
    isProfileComplete: boolean;
    missingFields: string[];
  }>('/users/me');
  return response.data!;
}

/**
 * Update user profile
 */
export async function updateProfile(data: {
  name?: string;
  phone?: string;
  companyName?: string;
  designation?: string;
}): Promise<User> {
  const response = await api.patch<User>('/users/me', data);
  return response.data!;
}

// completeProfile commented out for auth overhaul — easy re-enable
// export async function completeProfile(data: { ... }): Promise<User> { ... }

/**
 * Get user activity
 */
export async function getUserActivity(): Promise<{
  inquiryCount: number;
  catalogueDownloads: number;
  wishlistCount: number;
  recentInquiries: Array<{
    id: string;
    referenceNo: string;
    type: string;
    status: string;
    createdAt: string;
  }>;
}> {
  const response = await api.get<{
    inquiryCount: number;
    catalogueDownloads: number;
    wishlistCount: number;
    recentInquiries: Array<{
      id: string;
      referenceNo: string;
      type: string;
      status: string;
      createdAt: string;
    }>;
  }>('/users/me/activity');
  return response.data!;
}
